import React, { useCallback, useEffect, useMemo, useState } from 'react';
import Spinner from 'react-bootstrap/Spinner';

import {
    listSuperAdminFeedback,
    type SuperAdminFeedbackSubmission,
} from '../api/superadmin';
import { fetchOrganizations } from '../api/organizations';
import CustomDropdown, { type CustomDropdownOption } from '../components/CustomDropdown';
import { useAuth } from '../contexts/AuthContext';
import './SuperAdminFeedback-new.css';

type FeedbackCategoryFilter = 'all' | 'feedback' | 'bug' | 'suggestion';

const FILTER_TABS: { label: string; value: FeedbackCategoryFilter }[] = [
    { label: 'All', value: 'all' },
    { label: 'Feedback', value: 'feedback' },
    { label: 'Bugs', value: 'bug' },
    { label: 'Suggestions', value: 'suggestion' },
];

function formatDate(value: string | null): string
{
    if (!value)
    {
        return '—';
    }
    try
    {
        return new Date(value).toLocaleString(undefined, {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: 'numeric',
            minute: '2-digit',
        });
    }
    catch
    {
        return value;
    }
}

function badgeClass(category: FeedbackCategoryFilter): string
{
    switch (category)
    {
        case 'bug':
            return 'saf-badge saf-badge-bug';
        case 'suggestion':
            return 'saf-badge saf-badge-suggestion';
        case 'feedback':
            return 'saf-badge saf-badge-feedback';
        default:
            return 'saf-badge';
    }
}

const SuperAdminFeedbackPage: React.FC = () =>
{
    const { user } = useAuth();
    const [filter, setFilter] = useState<FeedbackCategoryFilter>('all');
    const [search, setSearch] = useState('');
    const [searchInput, setSearchInput] = useState('');
    const [orgFilterInput, setOrgFilterInput] = useState('');
    const [appliedOrgFilter, setAppliedOrgFilter] = useState('');
    const [orgOptions, setOrgOptions] = useState<CustomDropdownOption[]>([
        { value: '', label: 'All Organizations' },
    ]);
    const [items, setItems] = useState<SuperAdminFeedbackSubmission[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const activeOrgId = Number(user?.active_organization_id || user?.organization_id || 0) || 0;
    const hasActiveOrganizationScope = activeOrgId > 0;

    useEffect(() =>
    {
        if (hasActiveOrganizationScope)
        {
            setOrgFilterInput('');
            setAppliedOrgFilter('');
            return;
        }

        fetchOrganizations(1, 200)
            .then((data) =>
            {
                setOrgOptions([
                    { value: '', label: 'All Organizations' },
                    ...data.items.map((org) => ({
                        value: String(org.id),
                        label: org.name,
                    })),
                ]);
            })
            .catch(() => setOrgOptions([{ value: '', label: 'All Organizations' }]));
    }, [hasActiveOrganizationScope]);

    const loadFeedback = useCallback(async () =>
    {
        setLoading(true);
        setError(null);
        try
        {
            const response = await listSuperAdminFeedback({
                category: filter,
                search: search.trim() || undefined,
                organization_id: !hasActiveOrganizationScope && appliedOrgFilter ? Number(appliedOrgFilter) : undefined,
                page: 1,
                page_size: 100,
            });
            setItems(response.items);
        }
        catch (err: any)
        {
            setError(err?.response?.data?.detail || 'Failed to load feedback submissions');
        }
        finally
        {
            setLoading(false);
        }
    }, [appliedOrgFilter, filter, hasActiveOrganizationScope, search]);

    useEffect(() =>
    {
        loadFeedback();
    }, [loadFeedback]);

    const totalCount = useMemo(() => items.length, [items]);

    function handleSearchSubmit(event: React.FormEvent<HTMLFormElement>): void
    {
        event.preventDefault();
        setSearch(searchInput);
        setAppliedOrgFilter(orgFilterInput);
    }

    return (
        <div className="saf-page">
            <header className="saf-page-header">
                <div>
                    <h1 className="saf-page-title">User Feedback Inbox</h1>
                    <p className="saf-page-subtitle">
                        Review product feedback, bug reports, and suggestions from organization admins and users.
                    </p>
                </div>
                <div className="saf-filter-switcher" aria-label="Filter feedback">
                    {FILTER_TABS.map((tab) => (
                        <button
                            key={tab.value}
                            type="button"
                            className={`saf-filter-pill ${filter === tab.value ? 'saf-filter-pill-active' : ''}`}
                            onClick={() => setFilter(tab.value)}
                        >
                            {tab.label}
                        </button>
                    ))}
                </div>
            </header>

            <section className="saf-card">
                <div className="saf-card-header saf-card-header-stack">
                    <div>
                        <span className="saf-card-title">Submissions</span>
                        <span className="saf-card-count">{loading ? '…' : `${totalCount} total`}</span>
                    </div>
                    <form className="saf-search-form" onSubmit={handleSearchSubmit}>
                        {!hasActiveOrganizationScope && (
                            <div className="saf-search-org">
                                <CustomDropdown
                                    value={orgFilterInput}
                                    options={orgOptions}
                                    onChange={(value) => setOrgFilterInput(value)}
                                    placeholder="All Organizations"
                                />
                            </div>
                        )}
                        <input
                            className="saf-search-input"
                            value={searchInput}
                            onChange={(event) => setSearchInput(event.target.value)}
                            placeholder="Search subject, message, user, or organization"
                        />
                        <button type="submit" className="saf-search-btn">Search</button>
                    </form>
                </div>

                {loading ? (
                    <div className="saf-card-empty">
                        <Spinner animation="border" size="sm" /> Loading…
                    </div>
                ) : error ? (
                    <div className="saf-card-empty saf-card-error">{error}</div>
                ) : items.length === 0 ? (
                    <div className="saf-card-empty">No feedback submissions yet.</div>
                ) : (
                    <div className="saf-list">
                        {items.map((item) => (
                            <article key={item.id} className="saf-item">
                                <div className="saf-item-top">
                                    <div className="saf-item-heading">
                                        <span className={badgeClass(item.category)}>{item.category}</span>
                                        <h2 className="saf-item-title">{item.subject}</h2>
                                    </div>
                                    <div className="saf-item-date">{formatDate(item.created_at)}</div>
                                </div>

                                <div className="saf-item-meta">
                                    <span>{item.user_name || item.user_email}</span>
                                    <span>{item.user_email}</span>
                                    <span>{item.user_role}</span>
                                    <span>{item.organization_name || 'No organization selected'}</span>
                                    {item.page_url && <span>{item.page_url}</span>}
                                </div>

                                <p className="saf-item-message">{item.message}</p>
                            </article>
                        ))}
                    </div>
                )}
            </section>
        </div>
    );
};

export default SuperAdminFeedbackPage;
